import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import { db } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';
import { doc, setDoc } from 'firebase/firestore';

const RevisionRequestModal34 = () => {
  const {
    showRevisionRequestModal,
    setShowRevisionRequestModal,
    revisionRequestProject,
    setProjects,
    showNotification,
    setShowRevisionPaymentModal,
    setRevisionProject,
    setRevisionData,
    revisionData,
    documentTypes,
    getDocumentRevisionFee,
    currentUser
  } = useAppContext();

  const [revisionDetails, setRevisionDetails] = useState('');
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Add debug state
  const [debugInfo, setDebugInfo] = useState('');

  // Reset revision details when modal opens
  useEffect(() => {
    if (showRevisionRequestModal) {
      console.log('Modal opened, resetting form');
      setRevisionDetails('');
      setSupportingFiles([]);
      setDebugInfo('Modal opened');
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [showRevisionRequestModal]);

  // Debug log when supportingFiles changes
  useEffect(() => {
    console.log('Supporting files state updated:', supportingFiles);
    console.log('Number of files:', supportingFiles.length);
    if (supportingFiles.length > 0) {
      console.log('File names:', supportingFiles.map(f => f.name));
      setDebugInfo(`${supportingFiles.length} file(s) selected`);
    }
  }, [supportingFiles]);

  if (!showRevisionRequestModal || !revisionRequestProject) return null;

  // Only handle Stage 3 & 4 projects
  if (revisionRequestProject?.stage < 3) return null;

  // Test function to verify button works
  const testButtonClick = () => {
    console.log('===== TEST BUTTON CLICKED =====');
    console.log('Revision details:', revisionDetails);
    console.log('Supporting files:', supportingFiles);
    console.log('Current user:', currentUser);
    console.log('Revision data:', revisionData);
    alert('Button click works! Check console for details.');
  };

  // Upload files to storage with better error handling
  const uploadFilesToStorage = async (projectId, files) => {
    console.log('=== Starting file upload to Firebase Storage ===');
    console.log('Project ID:', projectId);
    console.log('Number of files to upload:', files.length);
    console.log('Storage instance:', storage);
    
    if (!storage) {
      console.error('❌ Firebase Storage is not initialized!');
      throw new Error('Firebase Storage is not initialized');
    }
    
    const uploadedDocs = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `projects/${projectId}/revision-documents/${timestamp}-${safeName}`;
        
        console.log(`Uploading file ${i + 1}/${files.length}: ${file.name}`);
        console.log('Storage path:', storagePath);
        console.log('File size:', file.size);
        console.log('File type:', file.type);
        
        const storageRef = ref(storage, storagePath);
        console.log('Storage reference created:', storageRef);
        
        const snapshot = await uploadBytes(storageRef, file);
        console.log('Upload snapshot:', snapshot);
        
        const downloadURL = await getDownloadURL(snapshot.ref);
        console.log('Download URL obtained:', downloadURL);
        
        uploadedDocs.push({
          name: file.name,
          size: file.size,
          type: file.type,
          uploadDate: new Date().toISOString(),
          url: downloadURL,
          storagePath: snapshot.ref.fullPath
        });
        
        console.log(`✅ File ${i + 1} uploaded successfully:`, file.name);
        setDebugInfo(`Uploaded ${i + 1}/${files.length} files`);
      } catch (error) {
        console.error(`❌ Error uploading file ${file.name}:`, error);
        console.error('Error details:', error.message);
        console.error('Error code:', error.code);
        console.error('Full error object:', error);
        
        // Still track the file even if upload failed
        uploadedDocs.push({
          name: file.name,
          size: file.size,
          type: file.type,
          uploadDate: new Date().toISOString(),
          url: null,
          error: error.message
        });
        setDebugInfo(`Upload failed: ${error.message}`);
      }
    }
    
    console.log('=== File upload complete ===');
    console.log('Successfully uploaded:', uploadedDocs.filter(d => d.url).length);
    console.log('Failed uploads:', uploadedDocs.filter(d => !d.url).length);
    
    return uploadedDocs;
  };

  // File selection handler
  const handleFileSelect = (e) => {
    console.log('=== File Selection Started ===');
    const selectedFiles = e.target.files;
    
    if (!selectedFiles || selectedFiles.length === 0) {
      console.log('No files selected');
      return;
    }

    const newFiles = Array.from(selectedFiles);
    console.log('Files selected:', newFiles.length);
    console.log('File details:', newFiles.map(f => ({
      name: f.name,
      size: `${(f.size / 1024 / 1024).toFixed(2)} MB`,
      type: f.type
    })));
    
    // Simplified validation - accept all files for testing
    setSupportingFiles(prevFiles => {
      const updatedFiles = [...prevFiles, ...newFiles];
      console.log('Updated supporting files state:', updatedFiles.length, 'files');
      return updatedFiles;
    });
    
    showNotification('Files Added', `${newFiles.length} file(s) added successfully`, 'success');
    
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    console.log('=== File Selection Complete ===');
  };

  // Remove file handler
  const removeFile = (indexToRemove) => {
    console.log('Removing file at index:', indexToRemove);
    setSupportingFiles(prev => {
      const newFiles = prev.filter((_, index) => index !== indexToRemove);
      console.log('Files after removal:', newFiles.length);
      return newFiles;
    });
  };

  const handleSubmitRevision = async () => {
    console.log('=== REVISION SUBMISSION STARTED ===');
    console.log('Button clicked at:', new Date().toISOString());
    console.log('Current user:', currentUser?.email);
    console.log('Revision details:', revisionDetails);
    console.log('Supporting files count:', supportingFiles.length);
    console.log('Document type:', revisionData?.documentType);
    
    setDebugInfo('Submission started...');
    
    try {
      if (!currentUser) {
        console.error('No authenticated user');
        showNotification("Authentication Error", "Please log in to submit revision", "error");
        setDebugInfo('Error: No authenticated user');
        return;
      }

      if (!revisionDetails.trim()) {
        console.error('No revision details provided');
        showNotification("Details Required", "Please describe your revision requirements", "error");
        setDebugInfo('Error: No revision details');
        return;
      }

      if (!revisionData?.documentType) {
        console.error('No document type selected');
        showNotification("Error", "No document type selected for revision", "error");
        setDebugInfo('Error: No document type');
        return;
      }

      const documentType = revisionData.documentType;
      const tracking = revisionRequestProject.documentRevisionTracking?.[documentType];
      const nextRevisionNumber = tracking ? tracking.revisionsUsed + 1 : 1;
      const isAdditionalRevision = tracking && nextRevisionNumber > tracking.maxIncluded;
      
      console.log('Next revision number:', nextRevisionNumber);
      console.log('Is additional revision:', isAdditionalRevision);
      setDebugInfo(`Processing revision ${nextRevisionNumber}...`);
      
      // For additional revisions that need payment
      if (isAdditionalRevision) {
        const paymentKey = `${documentType}_revision${nextRevisionNumber}PaymentStatus`;
        const isPaymentMade = revisionRequestProject[paymentKey] === 'paid';
        
        if (!isPaymentMade) {
          const revisionFee = getDocumentRevisionFee(
            revisionRequestProject.stage, 
            documentType, 
            nextRevisionNumber, 
            revisionRequestProject
          );
          
          console.log('Payment required. Storing revision data for payment flow...');
          setDebugInfo('Redirecting to payment...');
          
          setRevisionData({
            ...revisionData,
            revisionNumber: nextRevisionNumber,
            feedback: revisionDetails,
            supportingFiles: supportingFiles,
            fee: revisionFee,
            isAdditional: true
          });
          
          setRevisionProject(revisionRequestProject);
          setShowRevisionRequestModal(false);
          setShowRevisionPaymentModal(true);
          
          showNotification(
            "Payment Required", 
            `Additional revision fee of $${revisionFee} is required for ${revisionData.documentTypeLabel}`, 
            "info"
          );
          return;
        }
      }
      
      // Process the revision (included or already paid)
      setUploading(true);
      setDebugInfo('Uploading files...');
      console.log('Starting revision processing for included/paid revision...');
      
      // Upload supporting files if any
      let uploadedDocuments = [];
      if (supportingFiles.length > 0) {
        console.log(`Uploading ${supportingFiles.length} supporting files...`);
        uploadedDocuments = await uploadFilesToStorage(
          revisionRequestProject.id, 
          supportingFiles
        );
        console.log('Upload complete. Successful uploads:', uploadedDocuments.filter(d => d.url).length);
      } else {
        console.log('No supporting files to upload');
      }

      setDebugInfo('Saving to database...');

      // Update document revision tracking
      const updatedTracking = { ...revisionRequestProject.documentRevisionTracking };
      if (!updatedTracking[documentType]) {
        updatedTracking[documentType] = {
          revisionsUsed: 0,
          maxIncluded: 1,
          documents: []
        };
      }
      updatedTracking[documentType].revisionsUsed = nextRevisionNumber;
      
      const updatedProject = {
        ...revisionRequestProject,
        status: `revision-${nextRevisionNumber}-requested`,
        documentRevisionTracking: updatedTracking,
        communications: [
          ...(revisionRequestProject.communications || []),
          {
            type: 'client',
            sender: revisionRequestProject.client,
            message: `Revision ${nextRevisionNumber} requested for ${revisionData.documentTypeLabel}: ${revisionDetails}${uploadedDocuments.length > 0 ? ` (${uploadedDocuments.length} supporting file(s) attached)` : ''}`,
            timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
          },
          {
            type: "architect",
            sender: "planAIdesign",
            message: `We have received your revision request for ${revisionData.documentTypeLabel}${isAdditionalRevision ? ' (additional revision)' : ' (included revision)'}. We will begin working on it and provide an update within 3-5 business days.`,
            timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
          }
        ],
        revisionDocuments: [
          ...(revisionRequestProject.revisionDocuments || []),
          {
            documentType: documentType,
            documentTypeLabel: revisionData.documentTypeLabel,
            revisionNumber: nextRevisionNumber,
            requirements: revisionDetails,
            files: uploadedDocuments,
            uploadDate: new Date().toISOString(),
            isAdditionalRevision: isAdditionalRevision
          }
        ]
      };
      
      console.log('Saving to Firebase...');
      await setDoc(doc(db, 'projects', updatedProject.id), updatedProject);
      console.log('✅ Successfully saved to Firebase');
      
      // Update local state
      setProjects(prev => prev.map(p => 
        p.id === updatedProject.id ? updatedProject : p
      ));
      
      showNotification(
        "Revision Submitted", 
        `Your revision request for ${revisionData.documentTypeLabel} has been submitted successfully`, 
        "success"
      );

      setDebugInfo('Success! Closing modal...');

      // Reset form and close modal
      setRevisionDetails('');
      setSupportingFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setShowRevisionRequestModal(false);
      
      console.log('=== REVISION SUBMISSION COMPLETE ===');
      
    } catch (error) {
      console.error('❌ Error submitting revision:', error);
      console.error('Error details:', error.message);
      console.error('Full error:', error);
      setDebugInfo(`Error: ${error.message}`);
      showNotification("Error", `Failed to submit revision: ${error.message}`, "error");
    } finally {
      setUploading(false);
    }
  };

  // Determine revision info
  const getRevisionInfo = () => {
    if (!revisionData?.documentType) {
      return {
        nextRevisionNumber: 1,
        isAdditionalRevision: false,
        isPaymentMade: false,
        documentTypeLabel: 'Document',
        fee: 0
      };
    }
    
    const tracking = revisionRequestProject.documentRevisionTracking?.[revisionData.documentType];
    const nextRevisionNumber = tracking ? tracking.revisionsUsed + 1 : 1;
    const isAdditionalRevision = tracking && nextRevisionNumber > tracking.maxIncluded;
    const paymentKey = `${revisionData.documentType}_revision${nextRevisionNumber}PaymentStatus`;
    const isPaymentMade = revisionRequestProject[paymentKey] === 'paid';
    
    return {
      nextRevisionNumber,
      isAdditionalRevision,
      isPaymentMade,
      documentTypeLabel: revisionData.documentTypeLabel,
      fee: getDocumentRevisionFee(revisionRequestProject.stage, revisionData.documentType, nextRevisionNumber, revisionRequestProject)
    };
  };

  const revisionInfo = getRevisionInfo();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold">
            Request Revision {revisionInfo.nextRevisionNumber}
            {revisionInfo.documentTypeLabel && (
              <span className="text-lg text-gray-600 ml-2">
                - {revisionInfo.documentTypeLabel}
              </span>
            )}
          </h3>
          <button 
            onClick={() => setShowRevisionRequestModal(false)} 
            className="text-2xl text-slate-400 hover:text-slate-600"
          >✕</button>
        </div>

        {/* Debug Info Panel */}
        <div className="bg-yellow-100 border border-yellow-400 p-2 rounded mb-4">
          <p className="text-xs font-bold">Debug Info:</p>
          <p className="text-xs">{debugInfo}</p>
          <p className="text-xs">Files: {supportingFiles.length} | Text: {revisionDetails.length} chars</p>
        </div>

        <div className="mb-6">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <h4 className="font-semibold text-lg mb-2">{revisionRequestProject.name}</h4>
            <p className="text-sm text-gray-600">
              Stage {revisionRequestProject.stage} - {revisionRequestProject.stage === 3 ? 'Authority Approval' : 'Construction Documentation'}
            </p>
            <div className="mt-2 text-sm">
              <p><strong>Document Type:</strong> {revisionInfo.documentTypeLabel}</p>
              {!revisionInfo.isAdditionalRevision && (
                <p className="text-green-600 font-semibold mt-1">
                  This revision is included (no fee)
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block font-semibold mb-2">
                Revision Requirements <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="Please describe in detail what changes you would like made..."
                value={revisionDetails}
                onChange={(e) => {
                  setRevisionDetails(e.target.value);
                  console.log('Text updated:', e.target.value.length, 'chars');
                }}
                rows={6}
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                disabled={uploading}
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">
                Supporting Documents (Optional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="revision-file-upload"
                  accept="*/*"
                  disabled={uploading}
                />
                <label
                  htmlFor="revision-file-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <div className="text-3xl mb-2">📎</div>
                  <p className="text-gray-600 text-center">
                    Click to upload supporting files<br />
                    <span className="text-sm">PDF, JPG, PNG, DOC accepted</span>
                  </p>
                </label>
              </div>

              {supportingFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    Selected Files ({supportingFiles.length}):
                  </p>
                  {supportingFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📄</span>
                        <div>
                          <span className="text-sm font-medium">{file.name}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium px-2 py-1 rounded hover:bg-red-50"
                        disabled={uploading}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <button 
            onClick={() => {
              console.log('Cancel button clicked');
              setShowRevisionRequestModal(false);
            }}
            className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
            disabled={uploading}
          >
            Cancel
          </button>
          
          <button 
            onClick={testButtonClick}
            className="bg-yellow-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-yellow-600"
          >
            Test
          </button>
          
          <button 
            onClick={() => {
              console.log('Submit button clicked at:', new Date().toISOString());
              handleSubmitRevision();
            }}
            disabled={uploading || !revisionDetails.trim()}
            className="flex-1 bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 disabled:bg-gray-400"
          >
            {uploading ? 'Submitting...' : 'Submit Revision Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevisionRequestModal34;